import { ConfirmationModalComponent } from 'commons/components';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { showErrorNotification, showSuccessNotification } from '@root/src/core/utils/notification-service.ts';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { useTranslation } from 'core/i18n/i18n';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { executeRequest } from 'core/utils/backend-service';
import React from 'react';
import { usePopupManager } from 'react-popup-manager';
import { editRule, getAllRules } from '../service/settings-automation-rules.service';
import { TriggerActionsContainer } from '../containers/trigger-actions-container';
import { TriggerActionsLayout } from '../layouts/trigger-actions.layout';
import { Rule } from '../types/automation-rules.type';
import { AutomationRulesStepper } from '../ui-components/automation-rules-stepper';
import { TitleCard } from '../ui-components/title-card';
import { AutomationRulesLayout } from './automation-rules.layout';

export const AutomationRulesContainer: React.FC = () => {
    const { t } = useTranslation();
    const [isTriggerActionsProcessShown, setIsTriggerActionsProcessShown] = React.useState<boolean>(false);
    const selectedRuleRef = React.useRef<Rule | null>(null);
    const [rules, setRules] = React.useState<Rule[]>([]);
    const popupManager = usePopupManager();

    const { data: loadedRules, isFetching: isLoading } = usePocQuery<Rule[]>({
        queryKey: settingsKeys.automationRules(),
        queryFn: () => executeRequest(() => getAllRules().then((response) => response.data)),
    });

    // Seed the locally-mutable rules from the query (the enable/disable toggle updates them optimistically).
    React.useEffect(() => {
        if (loadedRules) {
            setRules(loadedRules);
        }
    }, [loadedRules]);

    const onCreateOptionHandler = () => {
        setIsTriggerActionsProcessShown(true);
    };

    const onCloseWithConfirmation = () => {
        showUnsavedChangesConfirmation();
    };

    const onCloseDirectly = () => {
        resetRule();
    };

    const showUnsavedChangesConfirmation = () => {
        popupManager.open(ConfirmationModalComponent, {
            title: t('settings.system.automation-rules.actions.discard-changes.modal.title'),
            children: (
                <PocTypography variant="subtitle1">
                    {t('settings.system.automation-rules.actions.discard-changes.modal.description')}
                </PocTypography>
            ),
            buttonText: t('settings.system.automation-rules.actions.discard-changes'),
            cancelButtonText: t('settings.system.automation-rules.actions.continue-editing'),
            actionOnClick: () => resetRule(),
        });
    };

    const refreshRules = () => updateQuery(settingsKeys.automationRules());

    const resetRule = () => {
        setIsTriggerActionsProcessShown(false);
        selectedRuleRef.current = null;
        return refreshRules();
    };

    const onEditOptionHandler = (id: string) => {
        const selectedRule = rules.find((rule) => rule.id === id) || null;

        setIsTriggerActionsProcessShown(() => {
            selectedRuleRef.current = selectedRule;
            return true;
        });
    };

    const onEnableChange = (id: string) => {
        const ruleToUpdate = rules.find((rule) => rule.id === id);
        if (!ruleToUpdate) {
            return;
        }

        const mode = ruleToUpdate.enabled ? 'disable' : 'enable';
        openConfirmationPopup(ruleToUpdate, mode);
    };

    const openConfirmationPopup = (ruleToUpdate: Rule, mode: 'enable' | 'disable') => {
        const translationKeyBase = `settings.system.automation-rules.actions.${mode}`;

        popupManager.open(ConfirmationModalComponent, {
            title: t(`${translationKeyBase}.modal.title`, { name: ruleToUpdate.name }),
            children: <PocTypography variant="subtitle1">{t(`${translationKeyBase}.modal.description`)}</PocTypography>,
            buttonText: t(`${translationKeyBase}`),
            cancelButtonText: t('settings.system.automation-rules.actions.cancel'),
            actionOnClick: () => updateEnabledRule(ruleToUpdate),
        });
    };

    const { mutateAsync: updateEnabledRuleMutation } = usePocMutation({
        mutationFn: (ruleToUpdate: Rule) =>
            editRule({ ...ruleToUpdate, enabled: !ruleToUpdate.enabled })
                .then(() => {
                    updateEnabledRulesState(ruleToUpdate);
                    // Reconcile the 5-min-cached rules query so a remount keeps the persisted enabled flag.
                    refreshRules();
                })
                .catch(() => {
                    if (ruleToUpdate.enabled) {
                        showErrorNotification(t('settings.system.automation-rules.actions.disable.notification.alert'));
                    } else {
                        showErrorNotification(t('settings.system.automation-rules.actions.enable.notification.alert'));
                    }
                }),
    });

    const updateEnabledRule = (ruleToUpdate: Rule) => updateEnabledRuleMutation(ruleToUpdate);

    const updateEnabledRulesState = (ruleToUpdate: Rule) => {
        setRules((prevRules) =>
            prevRules.map((rule) => (rule.id === ruleToUpdate.id ? { ...rule, enabled: !rule.enabled } : rule))
        );

        // activated -> deactivated
        if (ruleToUpdate.enabled) {
            showSuccessNotification(t(`settings.system.automation-rules.actions.disable.notification.positive`));
        } else {
            // deactivated -> activated
            showSuccessNotification(t('settings.system.automation-rules.actions.enable.notification.positive'));
        }
    };

    return (
        <>
            {isTriggerActionsProcessShown ? (
                <TriggerActionsContainer
                    onCloseWithConfirmation={onCloseWithConfirmation}
                    onCloseDirectly={onCloseDirectly}
                    defaultOption={selectedRuleRef.current}
                >
                    <TriggerActionsLayout showResetChanges={selectedRuleRef.current !== null}>
                        <TitleCard />
                        <AutomationRulesStepper />
                    </TriggerActionsLayout>
                </TriggerActionsContainer>
            ) : (
                <AutomationRulesLayout
                    isLoading={isLoading}
                    rules={rules}
                    onEnableChange={onEnableChange}
                    onCreateOptionHandler={onCreateOptionHandler}
                    onEditOptionHandler={onEditOptionHandler}
                />
            )}
        </>
    );
};
